/**
 * Reusable voxel-field queries: raycast (build targeting / punch / spawn probes / future
 * hitscan), ground height (spawn), submersion (swim detection), sphere cast (projectiles).
 *
 * All queries are pure reads, allocate nothing (caller-provided out objects), and treat
 * UNKNOWN chunks conservatively: a ray STOPS at an unknown boundary (material = -1) —
 * nothing may shoot, target, or spawn through unstreamed space.
 */
import { VOXEL_SCALE, MATERIAL_SHIFT, MATERIAL_MASK, CHUNK_SHIFT } from '../voxel/constants.js';
import { INV_VOXEL_SCALE, CHUNK_UNKNOWN, } from './voxelField.js';
const grad = { x: 0, y: 0, z: 0 };
const colHit = { found: false, y: 0, startsSolid: false };
/**
 * Amanatides & Woo DDA over voxel cells with per-cell sampled refinement.
 *
 * `mask` selects which material types count as hittable (RAY_SOLID | RAY_LIQUID |
 * RAY_TRANSPARENT). Returns true on a hit with `out` filled; false when the ray exhausted
 * `maxDist` (out untouched except t/material) or entered an UNKNOWN chunk
 * (out.material = -1, out.t = distance to that boundary).
 */
export function raycastVoxels(field, ox, oy, oz, dx, dy, dz, maxDist, mask, out) {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    out.material = 0;
    out.t = maxDist;
    if (len < 1e-12 || maxDist <= 0)
        return false;
    const ux = dx / len, uy = dy / len, uz = dz / len;
    // Grid-space ray (cells are unit cubes at integer corners)
    const gx0 = ox * INV_VOXEL_SCALE, gy0 = oy * INV_VOXEL_SCALE, gz0 = oz * INV_VOXEL_SCALE;
    let ix = Math.floor(gx0), iy = Math.floor(gy0), iz = Math.floor(gz0);
    const stepX = ux > 0 ? 1 : ux < 0 ? -1 : 0;
    const stepY = uy > 0 ? 1 : uy < 0 ? -1 : 0;
    const stepZ = uz > 0 ? 1 : uz < 0 ? -1 : 0;
    // Parametric distance (in meters) to cross one cell along each axis
    const tDeltaX = stepX !== 0 ? VOXEL_SCALE / Math.abs(ux) : Infinity;
    const tDeltaY = stepY !== 0 ? VOXEL_SCALE / Math.abs(uy) : Infinity;
    const tDeltaZ = stepZ !== 0 ? VOXEL_SCALE / Math.abs(uz) : Infinity;
    let tMaxX = stepX !== 0 ? ((stepX > 0 ? ix + 1 - gx0 : gx0 - ix) * VOXEL_SCALE) / Math.abs(ux) : Infinity;
    let tMaxY = stepY !== 0 ? ((stepY > 0 ? iy + 1 - gy0 : gy0 - iy) * VOXEL_SCALE) / Math.abs(uy) : Infinity;
    let tMaxZ = stepZ !== 0 ? ((stepZ > 0 ? iz + 1 - gz0 : gz0 - iz) * VOXEL_SCALE) / Math.abs(uz) : Infinity;
    let tEnter = 0;
    let guard = 0;
    const guardMax = (maxDist * INV_VOXEL_SCALE + 8) * 3;
    while (tEnter < maxDist && guard++ < guardMax) {
        // Unknown chunk → stop at its boundary
        if (field.chunkState(ix >> CHUNK_SHIFT, iy >> CHUNK_SHIFT, iz >> CHUNK_SHIFT) === CHUNK_UNKNOWN) {
            out.material = -1;
            out.t = tEnter;
            return false;
        }
        const tExit = Math.min(tMaxX, tMaxY, tMaxZ, maxDist);
        // Whole-cell air skip via corner convexity
        const c000 = field.cornerMaskedW(ix, iy, iz, mask);
        const c100 = field.cornerMaskedW(ix + 1, iy, iz, mask);
        const c010 = field.cornerMaskedW(ix, iy + 1, iz, mask);
        const c110 = field.cornerMaskedW(ix + 1, iy + 1, iz, mask);
        const c001 = field.cornerMaskedW(ix, iy, iz + 1, mask);
        const c101 = field.cornerMaskedW(ix + 1, iy, iz + 1, mask);
        const c011 = field.cornerMaskedW(ix, iy + 1, iz + 1, mask);
        const c111 = field.cornerMaskedW(ix + 1, iy + 1, iz + 1, mask);
        if (c000 >= 0 || c100 >= 0 || c010 >= 0 || c110 >= 0 || c001 >= 0 || c101 >= 0 || c011 >= 0 || c111 >= 0) {
            // Mixed/solid cell: sample the ray interval at 4 points, bisect the first straddle
            let tPrev = tEnter;
            let wPrev = sampleMasked(field, ox + ux * tPrev, oy + uy * tPrev, oz + uz * tPrev, mask);
            if (wPrev >= 0 && tEnter === 0) {
                // Ray starts inside a hittable volume — immediate hit at origin
                fillHit(field, out, ox, oy, oz, ux, uy, uz, 0, ix, iy, iz, mask);
                return true;
            }
            const span = tExit - tEnter;
            for (let i = 1; i <= 3; i++) {
                const tNext = i === 3 ? tExit : tEnter + (span * i) / 3;
                const wNext = sampleMasked(field, ox + ux * tNext, oy + uy * tNext, oz + uz * tNext, mask);
                if (wPrev < 0 && wNext >= 0) {
                    let a = tPrev, b = tNext;
                    for (let it = 0; it < 20; it++) {
                        const m = (a + b) * 0.5;
                        const wm = sampleMasked(field, ox + ux * m, oy + uy * m, oz + uz * m, mask);
                        if (wm >= 0)
                            b = m;
                        else
                            a = m;
                    }
                    fillHit(field, out, ox, oy, oz, ux, uy, uz, b, ix, iy, iz, mask);
                    return true;
                }
                tPrev = tNext;
                wPrev = wNext;
            }
        }
        // Advance to the next cell
        if (tMaxX <= tMaxY && tMaxX <= tMaxZ) {
            tEnter = tMaxX;
            tMaxX += tDeltaX;
            ix += stepX;
        }
        else if (tMaxY <= tMaxZ) {
            tEnter = tMaxY;
            tMaxY += tDeltaY;
            iy += stepY;
        }
        else {
            tEnter = tMaxZ;
            tMaxZ += tDeltaZ;
            iz += stepZ;
        }
        if (tEnter >= maxDist)
            break;
    }
    out.t = maxDist;
    return false;
}
function sampleMasked(field, x, y, z, mask) {
    const u = x * INV_VOXEL_SCALE, v = y * INV_VOXEL_SCALE, w = z * INV_VOXEL_SCALE;
    const gx = Math.floor(u), gy = Math.floor(v), gz = Math.floor(w);
    const fx = u - gx, fy = v - gy, fz = w - gz;
    const w000 = field.cornerMaskedW(gx, gy, gz, mask);
    const w100 = field.cornerMaskedW(gx + 1, gy, gz, mask);
    const w010 = field.cornerMaskedW(gx, gy + 1, gz, mask);
    const w110 = field.cornerMaskedW(gx + 1, gy + 1, gz, mask);
    const w001 = field.cornerMaskedW(gx, gy, gz + 1, mask);
    const w101 = field.cornerMaskedW(gx + 1, gy, gz + 1, mask);
    const w011 = field.cornerMaskedW(gx, gy + 1, gz + 1, mask);
    const w111 = field.cornerMaskedW(gx + 1, gy + 1, gz + 1, mask);
    const a0 = w000 + (w100 - w000) * fx;
    const a1 = w010 + (w110 - w010) * fx;
    const b0 = w001 + (w101 - w001) * fx;
    const b1 = w011 + (w111 - w011) * fx;
    const c0 = a0 + (a1 - a0) * fy;
    const c1 = b0 + (b1 - b0) * fy;
    return c0 + (c1 - c0) * fz;
}
function fillHit(field, out, ox, oy, oz, ux, uy, uz, t, cellX, cellY, cellZ, mask) {
    out.t = t;
    out.x = ox + ux * t;
    out.y = oy + uy * t;
    out.z = oz + uz * t;
    // Normal = -gradient of the raw solid field at the hit point (masked gradient would be
    // discontinuous at material-type boundaries; the solid field matches the visible surface).
    field.gradSolid(out.x, out.y, out.z, grad);
    let mag = Math.sqrt(grad.x * grad.x + grad.y * grad.y + grad.z * grad.z);
    if (mag < 1e-9) {
        // Degenerate (saturated) — face the ray back
        out.nx = -ux;
        out.ny = -uy;
        out.nz = -uz;
    }
    else {
        mag = 1 / mag;
        out.nx = -grad.x * mag;
        out.ny = -grad.y * mag;
        out.nz = -grad.z * mag;
    }
    out.gx = cellX;
    out.gy = cellY;
    out.gz = cellZ;
    // Dominant material: the included-type corner of the hit cell with the highest weight
    let bestW = -Infinity;
    let bestMat = 0;
    for (let dz = 0; dz <= 1; dz++) {
        for (let dyc = 0; dyc <= 1; dyc++) {
            for (let dxc = 0; dxc <= 1; dxc++) {
                const w = field.cornerMaskedW(cellX + dxc, cellY + dyc, cellZ + dz, mask);
                if (w > bestW) {
                    bestW = w;
                    const v = field.voxelAt(cellX + dxc, cellY + dyc, cellZ + dz);
                    bestMat = v < 0 ? 0 : (v >> MATERIAL_SHIFT) & MATERIAL_MASK;
                }
            }
        }
    }
    out.material = bestMat;
}
/**
 * Ground height at (x, z): highest solid crossing scanning down from yStart, at most
 * maxDown meters. Works from raw chunk data — no mesh required. Returns false when no
 * ground was found in the window (including when the scan entered unknown chunks; the
 * column crossing reads unknown as solid, so a hit inside unknown space cannot happen —
 * the crossing would sit at the unknown boundary with startsSolid semantics instead).
 */
export function groundHeightAt(field, x, z, yStart, maxDown, out) {
    field.columnCrossingDown(x, z, yStart, yStart - maxDown, colHit);
    if (!colHit.found || colHit.startsSolid)
        return false;
    out.y = colHit.y;
    field.gradSolid(x, colHit.y, z, grad);
    const mag = Math.sqrt(grad.x * grad.x + grad.y * grad.y + grad.z * grad.z);
    if (mag < 1e-9) {
        out.nx = 0;
        out.ny = 1;
        out.nz = 0;
    }
    else {
        out.nx = -grad.x / mag;
        out.ny = -grad.y / mag;
        out.nz = -grad.z / mag;
    }
    // Material of the solid corner just below the crossing
    const gx = Math.floor(x * INV_VOXEL_SCALE);
    const gy = Math.floor(colHit.y * INV_VOXEL_SCALE);
    const gz = Math.floor(z * INV_VOXEL_SCALE);
    const v = field.voxelAt(gx, gy, gz);
    out.material = v < 0 ? 0 : (v >> MATERIAL_SHIFT) & MATERIAL_MASK;
    return true;
}
/**
 * Capsule submersion in liquid, from voxel data (immune to the water shader's vertex
 * wave displacement). `eyeY` is the eye position; the capsule spans
 * [eyeY - innerHeight - radius, eyeY + radius].
 */
export function sampleSubmersion(field, x, eyeY, z, innerHeight, radius, out) {
    const feetY = eyeY - innerHeight - radius;
    const topY = eyeY + radius;
    const height = topY - feetY;
    const feetPhi = field.sampleLiquid(x, feetY + 0.01, z);
    const eyePhi = field.sampleLiquid(x, eyeY, z);
    out.feetInLiquid = feetPhi >= 0;
    out.eyeInLiquid = eyePhi >= 0;
    // Highest liquid surface crossing along the capsule axis (piecewise-linear exact scan)
    let surfaceY = NaN;
    const kBot = Math.floor(feetY * INV_VOXEL_SCALE);
    const kTop = Math.floor(topY * INV_VOXEL_SCALE) + 1;
    let wAbove = field.sampleLiquid(x, kTop * VOXEL_SCALE, z);
    for (let k = kTop - 1; k >= kBot; k--) {
        const wHere = field.sampleLiquid(x, k * VOXEL_SCALE, z);
        if (wAbove < 0 && wHere >= 0) {
            const t = wAbove / (wAbove - wHere);
            surfaceY = (k + 1 - t) * VOXEL_SCALE;
            break;
        }
        wAbove = wHere;
    }
    if (!Number.isNaN(surfaceY)) {
        out.fraction = Math.max(0, Math.min(1, (surfaceY - feetY) / height));
    }
    else {
        // No crossing inside the capsule: fully in or fully out by the feet sample
        out.fraction = out.feetInLiquid ? 1 : 0;
    }
    if (out.feetInLiquid || out.eyeInLiquid) {
        // Report the DOMINANT LIQUID corner of the containing cell, not whichever corner the
        // sample point floors onto — that one is frequently the neighbouring rock (a shoreline
        // or bed cell mixes liquid and solid corners), which would name the wrong material.
        const sy = out.feetInLiquid ? feetY + 0.01 : eyeY;
        const gx = Math.floor(x * INV_VOXEL_SCALE);
        const gy = Math.floor(sy * INV_VOXEL_SCALE);
        const gz = Math.floor(z * INV_VOXEL_SCALE);
        let bestW = -Infinity;
        let bestMat = -1;
        for (let dz = 0; dz <= 1; dz++) {
            for (let dy = 0; dy <= 1; dy++) {
                for (let dx = 0; dx <= 1; dx++) {
                    const w = field.cornerLiquidW(gx + dx, gy + dy, gz + dz);
                    if (w > 0 && w > bestW) {
                        bestW = w;
                        const v = field.voxelAt(gx + dx, gy + dy, gz + dz);
                        bestMat = v < 0 ? -1 : (v >> MATERIAL_SHIFT) & MATERIAL_MASK;
                    }
                }
            }
        }
        out.liquidMaterial = bestMat;
    }
    else {
        out.liquidMaterial = -1;
    }
}
/**
 * Swept sphere along a ray (projectile-grade): stencil march + bisection. Radius should be
 * small (≤ ~0.15 m); this is not a player-movement tool.
 */
export function sphereCastVoxels(field, ox, oy, oz, dx, dy, dz, maxDist, radius, mask, out) {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 1e-12 || maxDist <= 0)
        return false;
    const ux = dx / len, uy = dy / len, uz = dz / len;
    const step = Math.max(0.01, Math.min(radius, VOXEL_SCALE) * 0.5);
    let prevT = 0;
    for (let t = 0;; t += step) {
        if (t > maxDist)
            t = maxDist;
        const cx = ox + ux * t, cy = oy + uy * t, cz = oz + uz * t;
        if (sphereTouches(field, cx, cy, cz, radius, mask)) {
            // Bisect [prevT, t] for the contact
            let a = prevT, b = t;
            for (let it = 0; it < 16; it++) {
                const m = (a + b) * 0.5;
                if (sphereTouches(field, ox + ux * m, oy + uy * m, oz + uz * m, radius, mask))
                    b = m;
                else
                    a = m;
            }
            const hx = ox + ux * b, hy = oy + uy * b, hz = oz + uz * b;
            fillHit(field, out, ox, oy, oz, ux, uy, uz, b, Math.floor(hx * INV_VOXEL_SCALE), Math.floor(hy * INV_VOXEL_SCALE), Math.floor(hz * INV_VOXEL_SCALE), mask);
            return true;
        }
        if (t >= maxDist)
            break;
        prevT = t;
    }
    out.t = maxDist;
    out.material = 0;
    return false;
}
function sphereTouches(field, x, y, z, r, mask) {
    if (sampleMasked(field, x, y, z, mask) >= 0)
        return true;
    if (sampleMasked(field, x + r, y, z, mask) >= 0)
        return true;
    if (sampleMasked(field, x - r, y, z, mask) >= 0)
        return true;
    if (sampleMasked(field, x, y + r, z, mask) >= 0)
        return true;
    if (sampleMasked(field, x, y - r, z, mask) >= 0)
        return true;
    if (sampleMasked(field, x, y, z + r, mask) >= 0)
        return true;
    if (sampleMasked(field, x, y, z - r, mask) >= 0)
        return true;
    return false;
}
//# sourceMappingURL=queries.js.map